(() => {
  'use strict';

  const VERSION = '11.88';
  const api = window.AONSK_INTEGRATED_PLANNERS;
  const Planner = api?.__test?.GardenPlanner;
  if (!Planner || Planner.prototype.__aonsk1188) return;
  const proto = Planner.prototype;
  proto.__aonsk1188 = true;

  const esc = (value = '') => String(value).replace(/[&<>'"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[ch]));
  const PHASES = [
    { label: 'Setup', short: 'Plan and Grow Locations', title: 'Set up the garden once', lead: 'Name the plan, choose the season, and describe each growing space before comparing crops.', parts: [['plan', 'Plan basics'], ['spaces', 'Grow Locations']] },
    { label: 'Choose', short: 'Crops and quantities', title: 'Choose crops and quantities together', lead: 'Recommendations, saved selections, bed assignments, plant counts, and packets stay on the same screen.', parts: [['crops', 'Find crops'], ['quantities', 'Set quantities']] },
    { label: 'Schedule & care', short: 'Dates, soil, water and scouting', title: 'Put timing and care in one place', lead: 'Review planting windows alongside soil, water capacity, pests, seasonal watchpoints, and practical preparation.', parts: [['calendar', 'Planting schedule'], ['soil-water', 'Soil, water and scouting']] },
    { label: 'Harvest & review', short: 'Seedkeeping, print and save', title: 'Finish with harvest and a readable plan', lead: 'Connect harvest and seedkeeping decisions, then review, print, copy, or download the complete plan.', parts: [['harvest', 'Harvest and seedkeeping'], ['review', 'Review and save']] }
  ];
  const originalMethods = {
    plan: proto.stepPlan,
    spaces: proto.stepSpaces,
    crops: proto.stepCrops,
    quantities: proto.stepQuantities,
    calendar: proto.stepCalendar,
    'soil-water': proto.stepSoilWater,
    harvest: proto.stepHarvest,
    review: proto.stepReview
  };

  function ensure(planner) {
    planner.state.version = VERSION;
  }

  function stripStageNavigation(html) {
    const marker = '<div class="aonsk-planner-stage-actions">';
    const start = html.lastIndexOf(marker);
    if (start < 0) return html;
    const divTags = /<\/?div\b[^>]*>/g;
    divTags.lastIndex = start;
    let depth = 0;
    let end = -1;
    let tag;
    while ((tag = divTags.exec(html))) {
      depth += tag[0].startsWith('</') ? -1 : 1;
      if (depth === 0) { end = divTags.lastIndex; break; }
    }
    if (end < 0) return html;
    const actions = html.slice(start, end)
      .replace(/<button\b[^>]*\bdata-planner-(?:prev|next)\b[^>]*>[\s\S]*?<\/button>/g, '')
      .replace('<span></span>', '');
    return html.slice(0, start) + (/<(?:button|a)\b/.test(actions) ? actions : '') + html.slice(end);
  }

  function decoratePart(html, id, partNumber, title) {
    return stripStageNavigation(html)
      .replace('<section class="aonsk-planner-stage">', `<section class="aonsk-planner-stage aonsk-phase-part" id="aonsk-phase-${esc(id)}" data-phase-part="${esc(id)}">`)
      .replace(/(<header class="aonsk-planner-stage-head"><p>)[\s\S]*?(<\/p>)/, `$1Part ${partNumber} of 2 · ${esc(title)}$2`);
  }

  function renderLogicalStep(planner, logicalStep, methodName, title) {
    const phaseStep = planner.step;
    planner.step = logicalStep;
    try {
      return decoratePart(originalMethods[methodName].call(planner), methodName, logicalStep % 2 + 1, title);
    } finally {
      planner.step = phaseStep;
    }
  }

  function phaseActions(phase) {
    const previous = phase > 0 ? '<button type="button" class="aonsk-planner-action aonsk-planner-action--secondary" data-planner-prev>Previous phase</button>' : '<span></span>';
    const next = phase < PHASES.length - 1 ? `<button type="button" class="aonsk-planner-action" data-planner-next>Next: ${esc(PHASES[phase + 1].label)}</button>` : '';
    return `<footer class="aonsk-phase-actions">${previous}<div>${next}</div></footer>`;
  }

  function compactName(record, crop) {
    const full = String(record?.title || crop.handle || 'Untitled crop');
    const pieces = full.split(/\s+[–—-]\s+/).map(item => item.trim()).filter(Boolean);
    const name = String(record?.cultivar || (pieces.length >= 3 ? pieces.slice(2).join(' – ') : full));
    const context = pieces.length >= 3 ? `${pieces[0].replace(/\s+Seeds$/i, '')} · ${pieces[1]}` : String(record?.common || record?.sci || 'Selected crop');
    return { full, name, context };
  }

  function selectedCrops(planner) {
    const used = planner.state.spaces.filter(space => planner.state.crops.some(crop => crop.spaceId === space.id)).length;
    const groups = planner.state.spaces.map(space => {
      const crops = planner.state.crops.filter(crop => crop.spaceId === space.id);
      const rows = crops.map(crop => {
        const record = api.resolveRecord(crop?.handle, crop?.recordSnapshot) || null;
        const title = compactName(record, crop);
        const availability = record?.available === true ? ['available', 'Available now'] : record?.available === false ? ['unavailable', 'Currently unavailable'] : ['unchecked', 'Availability not checked'];
        return `<li>
          <span class="aonsk-selection-status is-${availability[0]}" aria-hidden="true"></span>
          <span class="aonsk-selection-name" title="${esc(title.full)}"><strong>${esc(title.name)}</strong><small>${esc(title.context)} · ${esc(availability[1])}</small></span>
          <button type="button" data-remove-selected="${esc(crop.id)}" aria-label="Remove ${esc(title.full)} from ${esc(space.name)}">Remove</button>
        </li>`;
      }).join('');
      return `<details class="aonsk-selection-location${crops.length ? '' : ' is-empty'}"${crops.length ? ' open' : ''}>
        <summary><span><strong>${esc(space.name)}</strong><small>${crops.length ? `${crops.length} selected variet${crops.length === 1 ? 'y' : 'ies'}` : 'No crops selected yet'}</small></span><b>${crops.length}</b></summary>
        ${crops.length ? `<ul>${rows}</ul>` : '<p>Use the crop search above when you want to add something to this Grow Location.</p>'}
      </details>`;
    }).join('');
    return `<section class="aonsk-selection-summary" aria-labelledby="aonsk-selected-crops-heading">
      <header><div><p class="aonsk-edu-eyebrow">Your selections</p><h3 id="aonsk-selected-crops-heading">${planner.state.crops.length} variet${planner.state.crops.length === 1 ? 'y' : 'ies'} in ${used} of ${planner.state.spaces.length} Grow Location${planner.state.spaces.length === 1 ? '' : 's'}</h3></div><p>Selections are grouped in readable rows. Availability remains visible, and quantities can be edited directly below.</p></header>
      <div>${groups}</div>
    </section>`;
  }

  function replaceOldSelectionTray(html, replacement) {
    const start = html.indexOf('<div class="aonsk-selected-tray">');
    if (start < 0) return html;
    const markers = ['<section class="aonsk-microclimate-report"', '<div class="aonsk-bed-compatibility', '<section class="aonsk-bed-compatibility', '<div class="aonsk-planner-stage-actions">'];
    const ends = markers.map(marker => html.indexOf(marker, start + 1)).filter(index => index > start);
    const end = ends.length ? Math.min(...ends) : -1;
    return end > start ? html.slice(0, start) + replacement + html.slice(end) : html;
  }

  const baseStepCrops = proto.stepCrops;
  proto.stepCrops = function () {
    return replaceOldSelectionTray(baseStepCrops.call(this), selectedCrops(this));
  };
  originalMethods.crops = proto.stepCrops;

  proto.steps = function () {
    return PHASES.map(phase => [phase.label, phase.short]);
  };

  proto.renderStep = function () {
    ensure(this);
    const phaseIndex = Math.max(0, Math.min(PHASES.length - 1, Number(this.step) || 0));
    const phase = PHASES[phaseIndex];
    const logicalStart = phaseIndex * 2;
    const first = renderLogicalStep(this, logicalStart, phase.parts[0][0], phase.parts[0][1]);
    const second = renderLogicalStep(this, logicalStart + 1, phase.parts[1][0], phase.parts[1][1]);
    return `<section class="aonsk-planner-phase" data-phase-index="${phaseIndex}">
      <header class="aonsk-phase-header"><div><p>Phase ${phaseIndex + 1} of ${PHASES.length}</p><h2>${esc(phase.title)}</h2></div><p>${esc(phase.lead)}</p></header>
      <nav class="aonsk-phase-jumps" aria-label="Sections in this phase"><a href="#aonsk-phase-${esc(phase.parts[0][0])}">${esc(phase.parts[0][1])}</a><a href="#aonsk-phase-${esc(phase.parts[1][0])}">${esc(phase.parts[1][1])}</a></nav>
      <div class="aonsk-phase-sections">${first}${second}</div>
      ${phaseActions(phaseIndex)}
    </section>`;
  };

  const baseRender = proto.render;
  proto.render = function () {
    ensure(this);
    const result = baseRender.call(this);
    ensure(this);
    const app = this.root.querySelector('.aonsk-planner-app');
    if (app) {
      app.classList.add('aonsk-planner-app--v1188');
      app.dataset.plannerPhase = String((Number(this.step) || 0) + 1);
    }
    return result;
  };

  window.AONSK_GARDEN_BUILDER_1188 = { version: VERSION, phases: PHASES, selectedCrops };
  const refresh = () => document.querySelector('[data-planner-page="garden"] [data-planner-step].is-active')?.click();
  queueMicrotask(refresh);
  setTimeout(refresh, 0);
})();

